'use client'
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  useDisclosure,
  UseDisclosureProps,
} from '@core/ui-elements'
import { CardColors, cardColorSchemaList } from '@utils/styles'
import { ForwardedRef, forwardRef, ReactNode, useImperativeHandle } from 'react'

export interface IRightDrawer {
  header?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  color?: CardColors
  canClose?: boolean
  defaultIsOpen?: boolean
  handleClose?: () => void
}

const RightDrawer = forwardRef(
  (
    {
      header,
      children,
      footer,
      color = CardColors.grayWhite,
      canClose = true,
      defaultIsOpen = false,
      handleClose,
    }: IRightDrawer,
    ref: ForwardedRef<UseDisclosureProps | undefined>
  ) => {
    const { isOpen, onOpen, onClose } = useDisclosure()

    useImperativeHandle(ref, () => ({
      onOpen,
      onClose,
    }))

    const handleOnClose = () => {
      if (handleClose) {
        handleClose()
      }

      onClose()
    }

    const borderBottomColor =
      color === CardColors.grayWhite
        ? 'gray.200'
        : cardColorSchemaList[color].primary

    return (
      <Drawer
        autoFocus={false}
        isOpen={defaultIsOpen ? defaultIsOpen : isOpen}
        placement={'right'}
        returnFocusOnClose={false}
        size={'lg'}
        trapFocus={false}
        variant={'rightSide'}
        onClose={handleOnClose}
      >
        <DrawerOverlay />
        <DrawerContent>
          {header ? (
            <DrawerHeader
              backgroundColor={cardColorSchemaList[color].secondary}
              borderBottomColor={borderBottomColor}
              borderRightRadius={0}
            >
              <Flex>{header}</Flex>
              {canClose ? <DrawerCloseButton size="md" /> : null}
            </DrawerHeader>
          ) : null}
          <DrawerBody>{children}</DrawerBody>
          {footer ? <DrawerFooter>{footer}</DrawerFooter> : null}
        </DrawerContent>
      </Drawer>
    )
  }
)

RightDrawer.displayName = 'RightDrawer'

export default RightDrawer
